import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Shop', path: '/shop' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-[#FAF6F0]/95 backdrop-blur-md shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <img 
                src="https://customer-assets.emergentagent.com/job_prairie-grains/artifacts/4rxtb62e_Robert%20-Good%20Grains.jpg"
                alt="Good Grains Logo"
                className="w-16 h-16 rounded-full object-cover shadow-lg border-2 border-[#D4A84B] transition-transform duration-300 group-hover:scale-105"
                style={{ imageRendering: 'crisp-edges' }}
              />
              <span className={`font-serif text-2xl font-bold transition-colors duration-300 hidden sm:block ${
                isScrolled ? 'text-[#2D5016]' : 'text-white'
              }`}>
                Good Grains
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`relative font-medium text-sm uppercase tracking-wider transition-colors duration-300 ${
                    isScrolled
                      ? isActive(link.path)
                        ? 'text-[#2D5016]'
                        : 'text-[#3B2F1E] hover:text-[#2D5016]'
                      : isActive(link.path)
                        ? 'text-[#D4A84B]'
                        : 'text-white/90 hover:text-white'
                  }`}
                >
                  {link.name}
                  {isActive(link.path) && (
                    <span className={`absolute -bottom-1 left-0 right-0 h-0.5 ${
                      isScrolled ? 'bg-[#2D5016]' : 'bg-[#D4A84B]'
                    }`} />
                  )}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden md:block">
              <Link
                to="/shop"
                className={`px-6 py-2.5 rounded-lg font-semibold text-sm uppercase tracking-wide transition-all duration-300 ${
                  isScrolled
                    ? 'bg-[#A0522D] text-white hover:bg-[#8B4513] shadow-md hover:shadow-lg'
                    : 'bg-[#D4A84B] text-[#3B2F1E] hover:bg-[#c49a3d]'
                }`}
              >
                Shop Now
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden p-2 rounded-lg transition-colors ${
                isScrolled ? 'text-[#3B2F1E]' : 'text-white'
              }`}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-all duration-500 ${
          isMobileMenuOpen ? 'visible opacity-100' : 'invisible opacity-0'
        }`}
      >
        <div
          className="absolute inset-0 bg-black/50"
          onClick={() => setIsMobileMenuOpen(false)}
        />
        <div
          className={`absolute top-0 right-0 h-full w-80 bg-[#FAF6F0] shadow-2xl transition-transform duration-500 ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="p-6 pt-24">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`py-3 px-4 rounded-lg font-medium text-lg transition-all ${
                    isActive(link.path)
                      ? 'bg-[#2D5016] text-white'
                      : 'text-[#3B2F1E] hover:bg-[#2D5016]/10'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/shop"
                className="mt-4 py-3 px-4 rounded-lg bg-[#A0522D] text-white font-semibold text-center hover:bg-[#8B4513] transition-colors"
              >
                Shop Now
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
